// One image job, shared by Resize, Convert, Compress and Crop. The worker runs
// it with the browser's canvas; tests run it with a fake codec.
import { OUTPUT_FORMATS, type OutputFormat } from "@/lib/image/format";
import { setJpegDpi } from "@/lib/image/jpeg-dpi";
import { searchQuality } from "@/lib/image/quality";
import type { Rect, Size } from "@/lib/image/size";

export type Rotation = 0 | 90 | 180 | 270;

export type ImageJob = {
  /** Part of the upright source to keep; the whole image when missing. */
  crop?: Rect;
  /** Turned clockwise after cropping. */
  rotate?: Rotation;
  flipH?: boolean;
  flipV?: boolean;
  /** Final size in pixels; defaults to the (cropped, rotated) size. */
  width?: number;
  height?: number;
  format: OutputFormat;
  /** 0–1 for JPG and WebP. */
  quality?: number;
  /** Search the quality to stay at or under this many bytes (JPG/WebP). */
  maxBytes?: number;
  /** Fills see-through pixels when saving JPG. */
  background?: string;
  /** Print resolution written into JPGs. */
  dpi?: number;
};

export type ImageOutput = {
  blob: Blob;
  width: number;
  height: number;
  /** The JPG/WebP quality used. */
  quality?: number;
  /** False if `maxBytes` couldn't be reached even at the lowest quality. */
  fits: boolean;
};

/** Everything the canvas needs, with defaults filled in. */
export type DrawPlan = {
  crop: Rect;
  rotate: Rotation;
  flipH: boolean;
  flipV: boolean;
  width: number;
  height: number;
  /** null keeps transparency. */
  background: string | null;
};

export const DEFAULT_QUALITY = 0.92;

export function rotatedSize(size: Size, rotate: Rotation): Size {
  return rotate % 180 === 0 ? size : { width: size.height, height: size.width };
}

export function planDraw(source: Size, job: ImageJob): DrawPlan {
  // Not `...source`: an ImageBitmap's width/height are getters and don't spread.
  const crop = job.crop ?? {
    x: 0,
    y: 0,
    width: source.width,
    height: source.height,
  };
  const rotate = job.rotate ?? 0;
  const turned = rotatedSize(crop, rotate);
  return {
    crop,
    rotate,
    flipH: !!job.flipH,
    flipV: !!job.flipV,
    width: Math.max(1, Math.round(job.width ?? turned.width)),
    height: Math.max(1, Math.round(job.height ?? turned.height)),
    background: job.format === "jpg" ? (job.background ?? "#ffffff") : null,
  };
}

export interface ImageCodec<Source extends Size, Surface> {
  /** Upright (EXIF orientation applied). Throws for unreadable files. */
  decode(blob: Blob): Promise<Source>;
  render(source: Source, plan: DrawPlan): Surface;
  encode(
    surface: Surface,
    format: OutputFormat,
    quality: number,
  ): Promise<Blob>;
  release?(source: Source): void;
}

export async function processImage<Source extends Size, Surface>(
  blob: Blob,
  job: ImageJob,
  codec: ImageCodec<Source, Surface>,
): Promise<ImageOutput> {
  const source = await codec.decode(blob);
  try {
    return await drawAndEncode(source, job, codec);
  } finally {
    // Only after encoding: browsers may draw lazily, right before saving.
    codec.release?.(source);
  }
}

async function drawAndEncode<Source extends Size, Surface>(
  source: Source,
  job: ImageJob,
  codec: ImageCodec<Source, Surface>,
): Promise<ImageOutput> {
  const plan = planDraw(source, job);
  const surface = codec.render(source, plan);

  const { lossy, mime } = OUTPUT_FORMATS[job.format];
  const finish = async (out: Blob): Promise<Blob> => {
    if (job.format !== "jpg" || !job.dpi) return out;
    const bytes = new Uint8Array(await out.arrayBuffer());
    return new Blob([setJpegDpi(bytes, job.dpi) as Uint8Array<ArrayBuffer>], {
      type: mime,
    });
  };
  const encode = async (q: number) =>
    finish(await codec.encode(surface, job.format, q));

  const size = { width: plan.width, height: plan.height };
  if (lossy && job.maxBytes) {
    const r = await searchQuality(encode, job.maxBytes);
    return { ...size, blob: r.blob, quality: r.quality, fits: r.fits };
  }
  const quality = lossy ? (job.quality ?? DEFAULT_QUALITY) : undefined;
  const out = await encode(quality ?? 1);
  return {
    ...size,
    blob: out,
    quality,
    fits: !job.maxBytes || out.size <= job.maxBytes,
  };
}
